import { Box, GridLegacy, Typography } from '@mui/material'
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectTopLevelErrorFromState } from 'src/app/CloseVirtualCardContent/redux/closeVirtualCardContent.selectors'
import { ButtonDesigns } from 'src/components/Button/Button'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import {
  CONFIRM_DELETE_API_KEY_TEXT,
  ConfirmDeleteApiKeyText,
} from 'src/constants'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { useDeleteAPIKey } from 'src/queries/organizations/apikeys.queries'
import {
  selectAPIKeyToDeleteExpiresAtFromState,
  selectAPIKeyToDeleteFromState,
} from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface DeleteAPIKeyContentProps {
  closeDeleteAPIKey: () => void
}

const DeleteAPIKeyPopup: React.FC<DeleteAPIKeyContentProps> = ({
  closeDeleteAPIKey,
}): React.ReactElement => {
  const translate = useTranslate()

  const [confirmationText, setConfirmationText] = useState<string | undefined>()
  const apiKeyId = useSelector(selectAPIKeyToDeleteFromState)
  const expiry = useSelector(selectAPIKeyToDeleteExpiresAtFromState)
  const { mutate: deleteAPIKey, isPending: deleteAPIKeyPending } =
    useDeleteAPIKey()

  const error = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)

  const isConfirmationFormValid =
    confirmationText === ConfirmDeleteApiKeyText[locale]

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (apiKeyId && isConfirmationFormValid) {
      analytics?.track('delete api key', {
        id: apiKeyId,
      })
      deleteAPIKey({ body: { apiKeyId } })
    }
  }

  return (
    <Popup
      css={css`
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
      onClose={closeDeleteAPIKey}
    >
      {apiKeyId !== undefined ? (
        <ModalDialogForm
          formOnSubmit={handleFormSubmit}
          actionButtonLabel={
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {translate('Confirm and Delete')}
            </span>
          }
          actionButtonLoading={deleteAPIKeyPending}
          actionButtonDesign={ButtonDesigns.designRed}
          cancelButtonLabel={translate('Cancel')}
          showCancelAsLink
          isFormValid={isConfirmationFormValid}
          error={error}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 32px;
            `}
          >
            <Box>
              <span
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {translate('Delete API Key')}
              </span>
            </Box>
            <Box
              css={css`
                width: 100%;
              `}
            >
              <GridLegacy
                css={css`
                  width: 100%;
                `}
                container
                spacing={2}
                alignItems="center"
              >
                <GridLegacy item xs={8} style={{ textAlign: 'left' }}>
                  <Typography
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  >
                    {translate('API Key ID')}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    fontFamily={'monospace'}
                    noWrap
                  >
                    {apiKeyId}
                  </Typography>
                </GridLegacy>
                <GridLegacy item xs={4} style={{ textAlign: 'right' }}>
                  <Typography
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  >
                    {translate('Expiry (MM/DD/YY)')}
                  </Typography>
                  <Typography variant="body1" color="textPrimary">
                    {expiry}
                  </Typography>
                </GridLegacy>
              </GridLegacy>
            </Box>
            <Box>
              {translate(
                'Are you sure want to delete this API key? This action cannot be undone.',
              )}
            </Box>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 40px;
                width: 100%;
              `}
            >
              <TextInputField
                allCaps
                design={TextInputFieldDesigns.New}
                name="remove-confirmation"
                fieldLabel={
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('Confirm by typing ${delete} below:', {
                      delete: (
                        <span style={{ color: `${styleConstants.colors.red}` }}>
                          {translate(`"${CONFIRM_DELETE_API_KEY_TEXT}"`)}
                        </span>
                      ),
                    })}
                  </span>
                }
                placeholder={translateSingleString(
                  locale,
                  'Please type "DELETE"',
                )}
                onChange={(value: string): void =>
                  setConfirmationText(value.toUpperCase())
                }
              />
            </span>
          </div>
        </ModalDialogForm>
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default DeleteAPIKeyPopup
